import React, { useEffect, useState } from 'react';
import MessagesContainer from './components/body/MessagesContainer';
import { useAppSelector, useAppDispatch } from '../../../../store/hooks';
import { setInputMessage } from '../../../../store/chat/slice';
import submitInputMessage from '../../../../store/chat/submitInputMessage';
import styles from './CurrentChat.module.scss';
import { ConversationStatus } from '../../../../store/chat/types';
import EmptyState from './components/body/EmptyState';
import ChatInput from '../../../../components/ChatInput';
// import Examples from './components/chatInput/Examples';
import MessageLimitIndicator from './components/chatInput/MessageLimitIndicator';
import { getCompaniesByIds } from '../../../../api/companyApi';
import { selectHasNonEmbeddedSelection } from '../../../../store/company/slice';
import { trackEvent } from '../../../../services/tracking';

const CurrentChat: React.FC = () => {
  const { conversation, isLoadingConversation, inputMessage, formValues } = useAppSelector(
    (state) => state.chat,
  );
  const { selectedCompanies } = useAppSelector((state) => state.company);
  const hasNonEmbeddedSelection = useAppSelector(selectHasNonEmbeddedSelection);
  const dispatch = useAppDispatch();
  const DEFAULT_PLACEHOLDER = 'Enter a question about any companies / $tickers';
  const [placeholder, setPlaceholder] = useState(DEFAULT_PLACEHOLDER);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);

  const isNewConversation = !conversation?.messages || conversation.messages.length === 0;
  const awaitingResponse = conversation?.status === ConversationStatus.Answering;
  const isLoading = isLoadingConversation || awaitingResponse || isLoadingCompanies;

  const filteredCompanyIds = formValues.filteredCompanyIds;

  useEffect(() => {
    const fetchCompaniesForPlaceholder = async () => {
      const ids = filteredCompanyIds || [];

      if (!ids || ids.length === 0) {
        setPlaceholder(DEFAULT_PLACEHOLDER);
        return;
      }

      // If we already have all the companies in selected companies, don't need to fetch
      if (ids.length > 0 && selectedCompanies.length > 0) {
        const selectedIdSet = new Set(selectedCompanies.map((c) => c.id));
        const allCovered = ids.every((id) => selectedIdSet.has(id));
        if (allCovered) {
          const orderedCompanies = ids
            .map((id) => selectedCompanies.find((c) => c.id === id))
            .filter((c): c is NonNullable<typeof c> => !!c);
          const names = orderedCompanies.map((c) => `$${c.ticker}`).join(', ');
          setPlaceholder(`Enter a question about ${names}...`);
          return;
        }
      }

      // Otherwise fetch by ids (from conversation)
      setIsLoadingCompanies(true);
      try {
        const fetchedCompanies = await getCompaniesByIds(ids);
        if (fetchedCompanies.length > 0) {
          const companyNames = fetchedCompanies.map((company) => `$${company.ticker}`).join(', ');
          setPlaceholder(`Enter a question about ${companyNames}...`);
        } else {
          setPlaceholder(DEFAULT_PLACEHOLDER);
        }
      } catch (error) {
        console.error('Failed to fetch companies for placeholder', error);
        setPlaceholder('Enter a question about company...');
      } finally {
        setIsLoadingCompanies(false);
      }
    };

    fetchCompaniesForPlaceholder();
  }, [filteredCompanyIds]);

  return (
    <div className={`${styles.chatContainer} ${isNewConversation ? styles.newConversation : ''}`}>
      <div className={styles.chatContainerContent}>
        {isNewConversation ? (
          <EmptyState isLoading={isLoadingConversation} />
        ) : (
          <MessagesContainer
            messages={conversation?.messages || []}
            awaitingResponse={awaitingResponse}
          />
        )}
      </div>

      <div className={styles.inputSection}>
        <div className={styles.inputWrapper}>
          <ChatInput
            value={inputMessage}
            onChange={(val) => {
              dispatch(setInputMessage(val));
            }}
            onBlurChange={() => {
              if (inputMessage.trim()) {
                trackEvent('Chat Question Edited', {
                  question: inputMessage,
                  selectedCompanies: selectedCompanies.map((c) => c.ticker),
                });
              }
            }}
            onSubmit={() => {
              trackEvent('Chat Submit Message', {
                question: inputMessage,
                selectedCompanies: selectedCompanies.map((c) => c.ticker),
              });
              dispatch(submitInputMessage());
            }}
            isLoading={isLoading}
            disabled={hasNonEmbeddedSelection}
            disabledTooltip={
              hasNonEmbeddedSelection
                ? "One of the selected companies isn't searchable yet."
                : undefined
            }
            placeholder={placeholder}
          />
          <MessageLimitIndicator />
        </div>
        {/* <Examples visible={isNewConversation} /> */}
      </div>
    </div>
  );
};

export default CurrentChat;
